import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateHelper } from '@app/date-helper';
import { EnvironmentService } from '@app/environment.service';
import { SearchSuggestion } from '@app/explore/places.service';
import { LocaleService } from '@app/locale.service';
import { HostelSearchParams } from '@app/search.service';
import {
  BehaviorSubject,
  Observable,
  filter,
  interval,
  map,
  of,
  switchMap,
  take,
  tap,
  catchError,
} from 'rxjs';

export interface CreateCardResponse {
  brand: string;
  card_expiration: string;
  card_number: string;
  cardholder_name: string;
  createdAt: string;
  cvv: string;
  payment_method_id: string;
  skyflow_id: string;
  updatedAt: string;
  _id: string;
}
export interface PaymentIntent {
  _id: string;
  clientSecret: string;
  customerId: string;
  ephemeralKey: string;
  nextAction: null;
  paymentId: string;
  status: string;
}

export interface PaymentIntentOptions {
  amount: string;
  email: string;
  currencyCode: string;
  propertyThumbnailUrl: string;
  propertyName: string;
  paymentMethodId: string;
}

export enum HOSTEL_HYDRATION_LEVEL {
  NONE,
  PARTIAL,
  REF,
  FULL,
}

export interface TravelGuideResponse {
  success: boolean;
  data: CountryTravelGuide | CityTravelGuide | HostelTravelGuide;
}

export interface CountryTravelGuide {
  _id: string;
  country: string;
  guide: string;
  defaultCountryId: string;
}

export interface CityTravelGuide {
  _id: string;
  city: string;
  guide: string;
  defaultCountryId: string;
}

export interface HostelTravelGuide {
  _id: string;
  city: string;
  hostel: string;
  recommended_duration: { [lang: string]: string };
  getting_around: { [lang: string]: string };
  cash_importance: { [lang: string]: string };
  local_transport: { [lang: string]: string };
  ticket_purchase_info: { [lang: string]: string };
  sim_card_info: { [lang: string]: string };
  haggling_advice: { [lang: string]: string };
  describe_area: { [lang: string]: string };
  hostel_safety: { [lang: string]: string };
  emergency_info: { [lang: string]: string };
  city_friendly_for_first_timers: { [lang: string]: string };
  local_dishes: { [lang: string]: string };
  digital_nomad_spots: { [lang: string]: string };
  seasonal_highlight: { [lang: string]: string };
  nightlife_description: { [lang: string]: string };
  local_etiquette: { [lang: string]: string };
  top_recommendations: { [lang: string]: string };
  additional_comments: { [lang: string]: string };
  to_and_from_airport: { [lang: string]: string };
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private CACHE_TIMEOUT = 60_000;

  private cache: Record<string, { timestamp: number; res: any }> = {};
  private availabilityCache: Record<string, { timestamp: number; res: any }> =
    {}; // not in use. should we cache availability responses?

  public properties = new BehaviorSubject<any[]>([]);
  // todo move to idb or session storage to keep out of memory
  public propertiesCache = new Map();

  constructor(
    private http: HttpClient,
    private environment: EnvironmentService,
    private localeService: LocaleService
  ) {}

  init() {
    // clear cache on interval. stale items are not pulled from cache, so this is only for clearing memory, not for freshness.
    interval(this.CACHE_TIMEOUT)
      .pipe(
        tap((_) => {
          for (const id in this.cache) {
            if (
              this.cache[id].timestamp <
              new Date().getTime() - this.CACHE_TIMEOUT
            ) {
              delete this.cache[id];
            }
          }
          for (const id in this.availabilityCache) {
            if (
              this.availabilityCache[id].timestamp <
              new Date().getTime() - this.CACHE_TIMEOUT
            ) {
              delete this.availabilityCache[id];
            }
          }
        })
      )
      .subscribe();
  }

  public getHostelsByCity(
    city: string,
    skip: number = 0,
    limit: number = 12
  ): Observable<FeaturedListingsResponse> {
    const key = JSON.stringify({ city, skip, limit });
    if (this.cache[key]) {
      if (
        this.cache[key].timestamp >
        new Date().getTime() - this.CACHE_TIMEOUT
      ) {
        return of(this.cache[key].res).pipe(take(1));
      }
    }

    return this.http
      .get<FeaturedListingsResponse>(
        `${this.environment.apiUrl}/api/v1/common/hostel`,
        {
          params: {
            skip,
            limit,
            city,
          },
          headers: {
            'x-ignore-device-interceptor': 'true',
          },
        }
      )
      .pipe(
        tap((res) => {
          if (res.success) {
            this.cache[key] = {
              res,
              timestamp: new Date().getTime(),
            };

            for (let i = 0; i < res.data.data.length; i++) {
              const hostel = res.data.data[i];
              if (!this.propertiesCache.has(hostel._id)) {
                this.propertiesCache.set(hostel._id, {
                  data: hostel,
                  hydratedness: HOSTEL_HYDRATION_LEVEL.PARTIAL,
                  timestamp: new Date().getTime(),
                });
              }
            }
          }
        })
      );
  }

  public getLocationPhoto(
    name: string,
    orientation: 'landscape' | 'portrait',
    type: 'city' | 'country'
  ): Observable<LocationPhoto> {
    return this.http
      .get<LocationPhotoResponse>(
        `${this.environment.apiUrl}/api/v1/explore/photos/?orientation=${orientation}&type=${type}&name=${name}`
      )
      .pipe(
        tap((response) => {
          if (!response.success) {
            throw new Error('Failed to fetch location photo');
          }
        }),
        map((response) => response.data)
      );
  }

  public getBulkLocationPhoto(
    names: string[],
    orientation: 'landscape' | 'portrait',
    type: 'city' | 'country'
  ): Observable<LocationPhoto[]> {
    return this.http
      .get<BulkLocationPhotoResponse>(
        `${
          this.environment.apiUrl
        }/api/v1/explore/photos/bulk?orientation=${orientation}&type=${type}&names=${names.join(
          ','
        )}`
      )
      .pipe(
        tap((response) => {
          if (!response.success) {
            throw new Error('Failed to fetch location photos');
          }
        }),
        map((response) => response.data)
      );
  }

  public searchHostels(properties: {
    cityId: string;
    searchKey: string;
    checkInDate: string;
    checkOutDate: string;
    noOfGuests: number;
  }) {
    const key = JSON.stringify(properties);
    if (this.cache[key]) {
      if (
        this.cache[key].timestamp >
        new Date().getTime() - this.CACHE_TIMEOUT
      ) {
        return of(this.cache[key].res).pipe(take(1));
      }
    }
    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        return this.http.post<any>(
          `${this.environment.apiUrl}/api/v1/backpacker/hostel/search`,
          {
            ...properties,
            currency: currency.code,
            currencySymbolNative: currency.symbol_native,
            searchType: 1,
            limit: 12,
          }
        );
      }),
      tap((res) => {
        if (res.success) {
          this.cache[key] = {
            res,
            timestamp: new Date().getTime(),
          };
        }
      })
    );
  }

  public getGoogleReviews(hostelId: string): Observable<GoogleReviewsResponse> {
    return this.http.get<GoogleReviewsResponse>(
      `${this.environment.apiUrl}/api/v1/backpacker/hostel/${hostelId}/google-reviews`
    );
  }

  public getTravelGuide(
    type: 'country' | 'city' | 'hostel',
    key: string
  ): Observable<TravelGuideResponse> {
    return this.http
      .get<TravelGuideResponse>(
        `${this.environment.apiUrl}/api/v1/explore/travel-guides/?type=${type}&key=${key}`
      )
      .pipe(
        catchError((error) => {
          console.error('Error fetching travel guide:', error);
          return of({ success: false, data: null });
        })
      );
  }

  // can use ?exclude param with "and not in" to avoid hydrating city when not necessary
  public searchHostelsByCity(properties: { cityId: string; limit: number }) {
    const key = JSON.stringify(properties);
    if (this.cache[key]) {
      if (
        this.cache[key].timestamp >
        new Date().getTime() - this.CACHE_TIMEOUT
      ) {
        return of(this.cache[key].res).pipe(take(1));
      }
    }
    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        return this.http.post<any>(
          `${this.environment.apiUrl}/api/v1/backpacker/hostel/search-by-city?cityId=${properties.cityId}&limit=${properties.limit}`,
          {
            ...properties,
            currency: currency.code,
            currencySymbolNative: currency.symbol_native,
            searchType: 1,
            limit: 12,
          }
        );
      }),
      tap((res) => {
        if (res.success) {
          this.cache[key] = {
            res,
            timestamp: new Date().getTime(),
          };
        }
      })
    );
  }

  // todo cache hash of full req (or better yet put in sw)
  public getFeatured(
    skip: number = 0,
    limit: number = 12
  ): Observable<FeaturedListingsResponse> {
    const key = JSON.stringify({ path: 'featured', skip, limit });
    if (this.cache[key]) {
      if (
        this.cache[key].timestamp >
        new Date().getTime() - this.CACHE_TIMEOUT
      ) {
        return of(this.cache[key].res).pipe(take(1));
      }
    }
    return this.http
      .get<FeaturedListingsResponse>(
        `${this.environment.apiUrl}/api/v1/common/hostel/featured`,
        {
          params: { skip, limit },
          headers: {
            'x-ignore-device-interceptor': 'true',
          },
        }
      )
      .pipe(
        tap((res) => {
          if (res.success) {
            this.cache[key] = {
              res,
              timestamp: new Date().getTime(),
            };

            for (let i = 0; i < res.data.data.length; i++) {
              const hostel = res.data.data[i];
              if (!this.propertiesCache.has(hostel._id)) {
                this.propertiesCache.set(hostel._id, {
                  data: hostel,
                  hydratedness: HOSTEL_HYDRATION_LEVEL.PARTIAL,
                  timestamp: new Date().getTime(),
                });
              }
            }
          }
        })
      );
  }

  public getRecent(skip: number = 0, limit: number = 10) {
    return this.http.get(
      `${this.environment.apiUrl}/api/v1/account/backpacker/recent/list`,
      { params: { skip, limit } }
    );
  }
  public getExploreCities() {
    return this.http.get<ExploreCitiesResponse>(
      `${this.environment.apiUrl}/api/v1/backpacker/hostel/explore-cities`
    );
  }

  public getCities() {
    // return this.http.get<Response<CityRef[]>>(`${this.environment.apiUrl}/api/v1/common/cities`)
    return this.http.get(`${this.environment.apiUrl}/api/v1/common/cities`);
  }

  public getHostelsForCity(cityId: string) {
    return this.http.get<PaginatedResponse<Hostel>>(
      `${this.environment.apiUrl}/api/v1/backpacker/hostel/explore-cities`,
      { params: { cityId } }
    );
  }

  public getCurrencyRate(isoCode: string): Observable<CurrencyRateResponse> {
    return this.http.post<CurrencyRateResponse>(
      `${this.environment.apiUrl}/api/v1/backpacker/hostel/get-currency-rate`,
      { isoCode }
    );
  }

  public getProperty(id: string): Observable<Hostel> {
    if (this.propertiesCache.has(id)) {
      const cached = this.propertiesCache.get(id);
      if (
        cached.hydratedness >= HOSTEL_HYDRATION_LEVEL.FULL &&
        cached.timestamp > new Date().getTime() - this.CACHE_TIMEOUT
      ) {
        return of(cached.data);
      }
    }

    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        return this.http.get<HostelResp>(
          `${this.environment.apiUrl}/api/v1/common/hostel/${id}`,
          {
            params: {
              currency: currency.code,
              currencySymbolNative: currency.symbol_native,
            },
            headers: {
              'x-ignore-device-interceptor': 'true',
            },
          }
        );
      }),
      tap((res) => {
        if (res.success) {
          this.cache[id] = {
            res,
            timestamp: new Date().getTime(),
          };
        }

        const hostel = res.data;
        this.propertiesCache.set(hostel._id, {
          data: hostel,
          hydratedness: HOSTEL_HYDRATION_LEVEL.FULL,
          timestamp: new Date().getTime(),
        });
      }),
      map((res) => {
        return res.data;
      })
    );
  }

  public getAvailability(
    id: string,
    options: HostelSearchParams
  ): Observable<AllAvailabilityResponse> {
    // if (this.availabilityCache[id]) {
    //   if (this.availabilityCache[id].timestamp > ((new Date()).getTime() - this.CACHE_TIMEOUT)) {
    //     return of(this.availabilityCache[id].res).pipe(take(1));
    //   }
    // }
    const params: Partial<HostelSearchParams> = {};
    if (options.checkInDate) {
      params['checkInDate'] = DateHelper.normalizeDateToUTCString(
        options.checkInDate
      );
    }
    if (options.checkOutDate) {
      params['checkOutDate'] = DateHelper.normalizeDateToUTCString(
        options.checkOutDate
      );
    }
    if (options.noOfGuests) {
      params['noOfGuests'] = options.noOfGuests;
    } else {
      params['noOfGuests'] = 1;
    }

    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        return this.http.post<any>(
          `${this.environment.apiUrl}/api/v1/backpacker/hostel/${id}/get-availability`,
          {
            ...params,
            currency: currency.code,
          }
        );
      }),
      tap((res) => {
        // if (res.success) {
        //   this.availabilityCache[id] = {
        //     res,
        //     timestamp: (new Date()).getTime()
        //   };
        // }
      }),
      map((res) => {
        if (res.success) {
          return {
            ...res,
            data: {
              date_wise_availability: res.data.date_wise_availability.map(
                (d) => {
                  return {
                    ...d,
                    date: DateHelper.normalizeDateToLocalISOString(
                      DateHelper.formatDateToLocalString(new Date(d.date))
                    ),
                  };
                }
              ),
            },
          };
        }
        return res;
      })
    );
  }

  public checkAvailability(
    id: string,
    options: HostelSearchParams
  ): Observable<AvailabilityResponse> {
    const params: Partial<HostelSearchParams> = {};
    if (options.checkInDate) {
      params['checkInDate'] = DateHelper.normalizeDateToUTCString(
        options.checkInDate
      );
    }
    if (options.checkOutDate) {
      params['checkOutDate'] = DateHelper.normalizeDateToUTCString(
        options.checkOutDate
      );
    }
    if (options.noOfGuests) {
      params['noOfGuests'] = options.noOfGuests;
    } else {
      params['noOfGuests'] = 1;
    }

    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        return this.http.post<any>(
          `${this.environment.apiUrl}/api/v1/backpacker/hostel/${id}/check-availability`,
          {
            ...params,
            currency: currency.code,
          }
        );
      })
    );
  }

  public getAvailabilityGet(
    id: string,
    options: HostelSearchParams
  ): Observable<AvailabilityResponse> {
    const params: Partial<HostelSearchParams> = {};
    if (options.checkInDate) {
      params['checkInDate'] = DateHelper.normalizeDateToUTCString(
        options.checkInDate
      );
    }
    if (options.checkOutDate) {
      params['checkOutDate'] = DateHelper.normalizeDateToUTCString(
        options.checkOutDate
      );
    }
    if (options.noOfGuests) {
      params['noOfGuests'] = options.noOfGuests;
    }

    if (!params.noOfGuests) {
      params.noOfGuests = 1;
    }

    // if (this.availabilityCache[id]) {
    //   if (this.availabilityCache[id].timestamp > ((new Date()).getTime() - this.CACHE_TIMEOUT)) {
    //     return of(this.availabilityCache[id].res).pipe(take(1));
    //   }
    // }
    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        return this.http.get<any>(
          `${this.environment.apiUrl}/api/v1/backpacker/hostel/${id}/get-availability`,
          {
            params: {
              ...params,
              currency: currency.code,
            },
          }
        );
      })
      // .pipe(
      // tap(res => {
      //   if (res.success) {
      //     this.availabilityCache[id] = {
      //       res,
      //       timestamp: (new Date()).getTime()
      //     };
      //   }
      // })
    );
  }

  public lockBooking(
    id: string,
    options: HostelSearchParams & { rooms: any }
  ): Observable<AvailabilityResponse> {
    const diffTime = Math.abs(
      new Date(options.checkOutDate!).valueOf() -
        new Date(options.checkInDate!).valueOf()
    );
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        const params = {
          property: id,
          checkInDate: DateHelper.normalizeDateToUTCString(
            options.checkInDate!
          ),
          checkOutDate: DateHelper.normalizeDateToUTCString(
            options.checkOutDate!
          ),
          currency: currency.code,
          currencySymbol: currency.symbol_native,
          noOfGuests: options.noOfGuests ?? 1,
          noOfNights: diffDays,
          rooms: {
            privateRooms: options.rooms.privateRooms.map((room: any) => {
              return {
                ...room,
                currency: currency.code,
              };
            }),
            dormRooms: options.rooms.dormRooms.map((room: any) => {
              return {
                ...room,
                currency: currency.code,
              };
            }),
            familyRooms: options.rooms.familyRooms.map((room: any) => {
              return {
                ...room,
                currency: currency.code,
              };
            }),
          },
        };

        return this.http.post<any>(
          `${this.environment.apiUrl}/api/v1/backpacker/guest/booking/lock`,
          params
        );
      })
    );
  }

  public addGuestCard(options: any): Observable<CreateCardResponse> {
    return this.http
      .post<Response<CreateCardResponse>>(
        `${this.environment.apiUrl}/api/v1/backpacker/guest/payment/card`,
        {
          card_number: options.card_number,
          cvv: options.cvv,
          card_expiration: options.card_expiration,
          brand: options.brand,
          skyflow_id: options.skyflow_id,
          cardholder_name: options.cardholder_name,
        }
      )
      .pipe(map((resp) => resp.data));
  }

  public createGuestPaymentIntent(
    options: PaymentIntentOptions
  ): Observable<PaymentIntentResponse> {
    return this.http
      .post<Response<PaymentIntentResponse>>(
        `${this.environment.apiUrl}/api/v1/backpacker/guest/payment/create`,
        {
          amount: options.amount,
          email: options.email,
          currency_code: options.currencyCode,
          property_thumbnail_url: options.propertyThumbnailUrl,
          property_name: options.propertyName,
          payment_method_id: options.paymentMethodId,
        }
      )
      .pipe(map((resp) => resp.data));
  }

  // todo separate by hydration level, since ref can be stored for significantly longer than full.
  public getPropertyRef(id: string): Observable<any> {
    if (this.propertiesCache.has(id)) {
      const cached = this.propertiesCache.get(id);
      if (
        cached.hydratedness >= HOSTEL_HYDRATION_LEVEL.REF &&
        cached.timestamp > new Date().getTime() - this.CACHE_TIMEOUT * 5
      ) {
        return of(cached.data);
      }
    }
    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        return this.http.get<any>(
          `${this.environment.apiUrl}/api/v1/common/hostel/${id}/ref`,
          {
            params: {
              currency: currency.code,
              currencySymbolNative: currency.symbol_native,
            },
            headers: {
              'x-ignore-device-interceptor': 'true',
            },
          }
        );
      }),
      tap((res) => {
        if (res.success) {
          this.cache[id] = {
            res,
            timestamp: new Date().getTime(),
          };

          const hostel = res.data;
          // if (!this.propertiesCache.has(hostel._id)) {
          this.propertiesCache.set(hostel._id, {
            data: hostel,
            hydratedness: HOSTEL_HYDRATION_LEVEL.REF,
            timestamp: new Date().getTime(),
          });
        }
      }),
      map((res) => {
        return res.data;
      })
    );
  }

  // todo just use currency, property name, property thumbnail, payment amount from the booking
  public confirmBooking(options: ConfirmBookingOptions) {
    return this.localeService.currentCurrency.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => {
        return this.http.post<any>(
          `${
            this.environment.vercelApiUrl ?? window.location.origin
          }/api/confirm-booking`,
          {
            ...options,
            currencyCode: currency.code,
          }
        );
      })
    );
  }
}
// todo just use currency, property name, property thumbnail url, and payable amount from the booking
export interface ConfirmBookingOptions {
  cardHolderName: string;
  firstName: string;
  lastName: string;
  gender: number;
  nationality: string;
  phone: { country_code: string; number: string };
  cardNumber: string;
  cardExpiration: string;
  cvv: string;
  bookingId: string;
  email: string;
  payableAmount: number;
  propertyThumbnailUrl: string;
  propertyName: string;
}

export interface Response<T> {
  success: boolean;
  data: T;
}

export interface PaginatedData<T> {
  data: T[];
  skip: number;
  limit: number;
  total: number;
}

export interface PaginatedResponse<T> extends Response<PaginatedData<T>> {}

export class Address {
  placeId: string;
  geoAddress: string;
  geoLocation: [number, number];
  country: string;
  state: string;
  city: string;
  zipCode: string;
  cityCenterGeoLocation: [number, number];
  countryId: string;
  cityId: string;
  stateId: string;
}

export interface ImageRef {
  _id: string;
  fileName: string;
  imageLink: string;
  thumbnailImageLink: string;
  isCoverImage: boolean;
}

export interface HostelRef {
  address: Address;
  _id: string;
  name: string;
  propertyImages: ImageRef[];
}

export interface FeaturedListing extends HostelRef {
  totalReviews?: number;
  overallRating: number;
  category: number;
  propertyRecommendationCount?: number;
}

export interface FeaturedListingsResponse
  extends PaginatedResponse<FeaturedListing> {}

export interface HostelRef {
  name: string;
  id: string;
}
export interface GoogleReviewsResponse {
  success: boolean; // Indicates whether the request was successful
  data: GoogleReviews; // Contains the actual Google reviews data
}

export class ExploreCityRef implements SearchSuggestion {
  cityId: string;
  stateId: string;
  countryId: string;
  display: string;
  cityName: string;
  countryName: string;
  url: string;

  get id() {
    return this.cityId;
  }

  get searchType() {
    return 1;
  }

  get name() {
    return this.cityName;
  }
}

export interface ExploreCitiesResponse
  extends PaginatedResponse<ExploreCityRef> {}

export class CityRef implements SearchSuggestion {
  cityId: string;
  stateId: string;
  countryId: string;
  cityName: string;
  countryName: string;
  url: string;
  id: string;
  get searchType() {
    return 1;
  }

  get display() {
    return `${this.cityName}, ${this.countryName}`;
  }
}

export interface CitiesResponse extends PaginatedResponse<CityRef> {}

export interface Currency {
  symbol_native: string;
  code: string;
  name: string;
}

export interface CurrenciesResponse extends Response<Currencies> {}

export interface Currencies {
  availableCurrencies: Currency[];
}

export interface CurrencyRate {
  rate: string;
}

export interface CurrencyRateResponse extends Response<CurrencyRate> {}

export interface HostelSearch {
  cityId: string;
  searchKey: string;
  searchType: number;
  checkInDate: string;
  checkOutDate: string;
  noOfGuests: number;
  currency: string;
  currencySymbolNative: string;
  filterOptions: {
    costPerNight: {
      min: number;
      max: number;
    };
    dormRoomGenders: any[];
    privateRoomCategories: any[];
    facilities: any[];
  };
  sort: {
    price: number;
    rating: number;
    distance: number;
  };
}

export interface AvailableDate {
  _id: string;
  date: string;
  available: boolean;
}

export interface AllAvailabilityResponse extends Response<AllAvailability> {}
export interface AllAvailability {
  date_wise_availability: AvailableDate[];
}

export interface AvailabilityResponse extends Response<Availability> {}
export interface Availability {
  _id: string;
  name: string;
  address: Address;
  checkInDate: string;
  checkOutDate: string;
  noOfGuests: number;
  noOfNights: number;
  rooms: Rooms;
}

export class BookingAmount {
  currency: any;
  payableAmount: any;
  subTotalAmount: any;
  totalAmount: any;
  cityTax: any;
  vat: any;
  otherTax: any;
  [key: string]: any;
}

export interface Booking {
  _id: string;
  property: any;
  rooms: any;
  bookingDate: any;
  checkInDate: any;
  checkOutDate: any;
  bookingAmount: [BookingAmount, BookingAmount];
  [key: string]: any;
  displayId?: string;
  guest?: BackpackerRef;
}

export interface Rooms {
  dormRooms: Array<Room>;
  privateRooms: Array<Room>;
  familyRooms: Array<Room>;
}
3;
export interface PaymentIntentResponse {
  clientSecret: string;
  customerId: string;
  ephemeralKey: string;
  nextAction: null;
  paymentId: string;
  status: string;
}

const mockRoom = (id: any) => ({
  _id: String(id),
  // restrictions: eachRoom.restrictions,
  // checkInDate: eachRoom.checkInDate,
  // checkOutDate: eachRoom.checkOutDate,
  standardRate: 21.8,
  minRate: 21.8,
  amenities: [],
  roomType: 2,
  privateRoomCategory: 1,
  numberOfRooms: 5,
  noOfAvailableBeds: 10,
  restrictions: {
    standard: {
      min: 21.8, // min and max won't work
      max: 25,
      stopSell: false,
    },
    nonRefundable: {
      min: 18.8,
      max: 25,
      stopSell: false,
    },
  },
  currency: '$',
  currencySymbolNative: '$',
  nonRefundableRate: 25,
  enSuite: false,
  description: 'test description',
  name: 'test room',
  occupancy: 10,
  roomImages: [
    {
      _id: '64f0d1f45328afb531169cfd',
      fileName: 'app/prod/property/1693503936573/llzgijhz0zshdf95dc0ltoef.png',
      imageLink:
        'https://storage.googleapis.com/hostel-hop-storage/app%2Fprod%2Fproperty%2F1693503936573%2Fllzgijhz0zshdf95dc0ltoef.png',
      thumbnailImageLink:
        'https://storage.googleapis.com/hostel-hop-storage/app%2Fprod%2Fproperty%2F1693503936575%2Fllzgikir0zshdf95dc5yzqef_thumb.png',
      isCoverImage: true,
    },
  ],
});

export const rooms: Rooms = {
  dormRooms: [{ ...mockRoom(1) }, { ...mockRoom(2) }],
  privateRooms: [{ ...mockRoom(3) }, { ...mockRoom(4) }],
  familyRooms: [{ ...mockRoom(5) }, { ...mockRoom(6) }],
};

export interface HostelResp {
  uuid: string;
  name: string;
  propertyType: {
    text: string;
    language: string;
  };
  description: {
    text: string;
    language: string;
  };
  // Map<String, String>
  descriptionTranslations: Map<string, string>;
  address: string;
  suburb: string;
  state: string;
  country: string;
  email: string;
  phoneNumber: string;
  language: string;
  latitude: number;
  longitude: number;
  checkInTime: string;
  checkOutTime: string;
  starRating: number;
  currency: string;
  photos: Array<{ url: string }>;
  amenities: Array<{ text: string; language: string }>;
  acceptedCardTypes: Array<string>;
  totalCommissionPercentage: number;
  siteminderCommissionPercentage: number;
  channelCommissionPercentage: number;
  roomTypes: Array<any>;
}
export interface Amenity {
  status: number;
  _id: string;
  names: { language: string; translation: string }[];
  icon: string;
}

export interface Badge {
  _id: string;
  name: string;
  icon: string;
  category: string;
  index: number;
  miniIcon: string;
}

export interface Review {
  status: number;
  badges: Badge[];
  _id: string;
  property: string;
  review: string;
  rating: number;
  backpacker: BackpackerRef;
  reviewDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface HostelResp {
  success: boolean;
  data: Hostel;
}

export interface Hostel {
  _id: string;
  name: string;
  checkInTime: string;
  checkOutTime: string;
  distanceFromCityCenter: number;
  websiteLink: string;
  propertyImages: ImageRef[];
  address: Address;
  cancellationPolicy: {
    noOfNights: number;
    cancelPrior: number;
  };
  cityTax: {
    type: number;
    value: number;
  };
  phone?: {
    country_code: string;
    number: string;
    iso_code: string;
  };
  totalReviews?: number;
  overallRating: number;
  recommendByBackpackersPercentage: number;
  amenities: Amenity[];
  description: string;
  // Map<String, String>
  descriptionTranslations: Map<string, string>;
  email: string;
  owner: {
    personalInfo: any;
    _id: string;
  };
  facebookLink: string;
  instagramLink: string;
  reviews: Review[];
  minRate: number;
  dormMinRate: number;
  currency: string;
  rooms: Room[];
  privateMinRate: number;
  isFavourite: boolean;
  propertyRecommendationCount?: number;
  recommendationsFromBackpackers: Recommendation[];
  // New attributes related to Google reviews
  googleReviewsSearched?: boolean; // Flag to indicate if Google reviews have been searched
  google_review_data?: GoogleReviewData;
  hyperguestPropertyId?: number;
}

export interface GoogleReviewData {
  rating?: number; // Average rating from Google
  reviews_per_score?: Record<string, number>; // Distribution of reviews per score (e.g., {"5": 10, "4": 5, ...})
  reviews_count?: number; // Total number of Google reviews
}

export interface GoogleReviews {
  property: string; // Reference to the property ID (ObjectId as a string)
  reviews_data: Array<GoogleReview>;
  rating: number; // Average rating for the property
  reviews_per_score: Record<string, number>; // Distribution of reviews per score
  reviews_count: number; // Total number of reviews
}

export interface GoogleReview {
  google_id: string; // The unique identifier for the Google place
  review_id: string; // The unique identifier for the review
  review_pagination_id: string; // The pagination ID for navigating reviews
  author_link: string; // URL to the author's Google profile
  author_title: string; // The display name of the author
  author_id: string; // The unique identifier for the author
  author_image: string; // URL to the author's profile image
  author_reviews_count: number; // Number of reviews written by the author
  author_ratings_count: number; // Number of ratings given by the author
  review_text: string; // The text content of the review
  review_img_urls: string[] | null; // An array of image URLs in the review or null if none
  review_img_url: string | null; // The primary image URL of the review or null if none
  review_questions: Record<string, any> | null; // Object containing any review questions or null
  review_photo_ids: string[] | null; // An array of photo IDs in the review or null if none
  owner_answer: string | null; // The response from the owner to the review or null
  owner_answer_timestamp: number | null; // Timestamp of the owner's answer or null
  owner_answer_timestamp_datetime_utc: string | null; // DateTime of the owner's answer in UTC or null
  review_link: string; // URL to the full review on Google
  review_rating: number; // The rating given in the review
  review_timestamp: number; // Unix timestamp of the review
  review_datetime_utc: string; // DateTime of the review in UTC format
  review_likes: number; // Number of likes the review received
  reviews_id: string; // A unique identifier for the review in the system
}

export interface BackpackerRef {
  _id: string;
  nationality: string;
  username: string;
  profilePicture: string;
  email?: string;
}

export interface Room {
  _id?: string;
  id?: string;
  standardRate: number;
  minRate: number;
  amenities: Amenity[];
  roomType: number;
  restrictions: {
    standard: {
      min: number;
      max: any;
      stopSell: boolean;
    };
    nonRefundable: {
      min: number;
      max: any;
      stopSell: boolean;
    };
  };
  currency: string;
  currencySymbolNative: string;
  nonRefundableRate: number;
  enSuite: boolean;
  description: string;
  name: string;
  roomImages: ImageRef[];
}

export interface Recommendation {
  status: number;
  _id: string;
  backpacker?: BackpackerRef;
  recommendationDate: string;
}

export interface LocationPhotoResponse {
  success: boolean;
  data: LocationPhoto;
}

export interface BulkLocationPhotoResponse {
  success: boolean;
  data: LocationPhoto[];
}

export interface LocationPhoto {
  _id: string;
  orientation: 'landscape' | 'portrait';
  imageUrl: string;
  photographer: string;
  unsplashLink: string;
  createdAt: string;
  defaultId: string;
  name: string;
  __v: number;
}
